import { reportTransportFailure } from '@parsina/framework';

import { getToken, isTokenExpired, redirectToLogin, refreshAccessToken } from './auth.js';
import { ADMIN_BFF_URL as BFF_URL } from './runtimeEnv.js';

/**
 * GraphQL client for the back office.
 *
 * Auth lives entirely in auth.js — the same OIDC + PKCE flow the clinic panel
 * uses, against the same Keycloak client — so there is exactly one place a
 * mistake could grant platform access.
 */

// ── Boot vs. mounted ─────────────────────────────────────────────────────────
//
// DESIGN-CONCEPT §13.1: "a session ending is an EVENT, not a navigation", and a
// global redirect on expiry is forbidden — it is a full-document navigation, so
// React unmounts and everything unsaved on screen is destroyed without warning.
// In this panel that is a typed suspension reason or a half-filled activation,
// both of which are audited writes an operator would have to reconstruct.
//
// The one place a redirect IS right is BOOT, where there is nothing on screen to
// lose. So the client has two modes, and `AuthGate` flips it once the app is
// actually rendering.

let mounted = false;

/** Called by AuthGate once the authenticated tree is on screen. */
export function markAppMounted() { mounted = true; }

/** Test seam: go back to boot semantics. */
export function __resetMountedForTests() { mounted = false; }

export const SESSION_ENDED_EVENT = 'parsina:session-ended';
export const FORBIDDEN_EVENT = 'parsina:forbidden';

function endSession() {
  if (mounted) {
    // Over the intact screen. Re-login returns to the same URL, so the operator
    // lands back where they were rather than on the dashboard.
    window.dispatchEvent(new Event(SESSION_ENDED_EVENT));
    return;
  }
  redirectToLogin();
}

async function validToken() {
  let token = getToken();
  if (!token) return null;
  if (isTokenExpired(token)) {
    const ok = await refreshAccessToken();
    if (!ok) return null;
    token = getToken();
  }
  return token;
}

/**
 * The one GraphQL transport, parameterised by endpoint.
 *
 * Token, silent refresh, the 401-retry and the session-ended event live HERE
 * and nowhere else: the CRM client (`crmApi.js`) is built from this same core
 * rather than a copy of it, so there is still exactly one place a mistake in
 * the auth path could live.
 *
 * Options — both default to the platform client's historic behaviour, which
 * `gql` below keeps byte for byte:
 *
 *   dispatchForbidden  a `FORBIDDEN` answer fires `parsina:forbidden`, which
 *                      AuthGate turns into the whole-panel 403. True for the
 *                      platform schema, where FORBIDDEN means "not staff". The
 *                      CRM client turns it off: there a FORBIDDEN is one
 *                      screen's problem (typically the Keycloak claim-location
 *                      trap), never a reason to take the panel away.
 *   unavailableCode    when set, a response that is not GraphQL-shaped JSON — or
 *                      a non-2xx with no `errors` — throws an Error with this
 *                      code instead of whatever `res.json()` happened to throw.
 *                      The screen then shows its error state, not a crash.
 */
export function createGqlClient(url, { dispatchForbidden = true, unavailableCode = null } = {}) {
  async function post(token, query, variables) {
    try {
      return await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ query, variables }),
      });
    } catch (err) {
      // A THROWN fetch is a transport failure — DNS, a dropped connection, a
      // firewall — as opposed to an HTTP error, which means the BFF answered. Only
      // the former is evidence the connection is degraded, and it is evidence the
      // browser itself cannot supply: `navigator.onLine` is reliable when it says
      // false and worthless when it says true, so a captive portal or a dead VPN
      // reports "online" while discarding every packet. This report is what lets
      // the ConnectivityBanner know the difference.
      reportTransportFailure();
      throw err;
    }
  }

  const unavailable = (message) => {
    const err = new Error(message);
    err.code = unavailableCode;
    return err;
  };

  return async function request(query, variables = {}) {
    const token = await validToken();
    if (!token) {
      endSession();
      const err = new Error('Not authenticated');
      err.code = 'UNAUTHENTICATED';
      throw err;
    }

    let res = await post(token, query, variables);

    // A 401 can also mean server-side expiry or clock skew; refresh once and retry
    // before troubling the operator at all.
    if (res.status === 401) {
      const refreshed = await refreshAccessToken();
      if (!refreshed) {
        endSession();
        const err = new Error('Session expired');
        err.code = 'UNAUTHENTICATED';
        throw err;
      }
      res = await post(getToken(), query, variables);
    }

    let json;
    if (unavailableCode) {
      try {
        json = await res.json();
      } catch {
        throw unavailable(`HTTP ${res.status}`);
      }
    } else {
      json = await res.json();
    }
    if (json.errors?.length) {
      const first = json.errors[0];
      const err = new Error(first.message);
      err.code = first.extensions?.code;
      // UNAUTHENTICATED means the token itself was rejected. FORBIDDEN is different
      // in kind: they ARE signed in but lack platform_admin, and logging in again
      // would produce the same token and the same rejection — so that one surfaces
      // as a page, never as a redirect, or it is an infinite loop that looks like a
      // broken site.
      if (err.code === 'UNAUTHENTICATED') endSession();
      else if (err.code === 'FORBIDDEN' && dispatchForbidden) window.dispatchEvent(new Event(FORBIDDEN_EVENT));
      throw err;
    }
    if (unavailableCode && !res.ok) throw unavailable(`HTTP ${res.status}`);
    return json.data;
  };
}

/** The platform schema, served by the admin BFF at its root. */
export const gql = createGqlClient(BFF_URL);

/**
 * The catalogue key for a BFF error, or null when there is nothing better to say
 * than the message the BFF sent.
 *
 * `BUSINESS_RULE` returns null deliberately: those messages are written by the
 * subscription service for an operator («این عملیات در وضعیت فعلی ممکن نیست»)
 * and are more specific than anything this client could substitute. They are
 * Persian-only, which is a known gap recorded in the redesign spec rather than
 * something the client can fix.
 */
export function errorKey(code) {
  switch (code) {
    case 'FORBIDDEN_DOWNSTREAM': return 'errors.code.forbiddenDownstream';
    case 'NOT_FOUND': return 'errors.code.notFound';
    case 'UNAVAILABLE': return 'errors.code.unavailable';
    case 'BUSINESS_RULE': return null;
    default: return null;
  }
}

/** A human message for an error, preferring the catalogue over the raw text. */
export function errorMessage(err, t) {
  const key = errorKey(err?.code);
  if (key) return t(key);
  return err?.message || t('errors.code.unknown');
}

export { getToken };
